/**
 * 「日期標籤」的格式化——**印 weekStart / weekEnd 一律走這裡**。
 *
 * ⛔ 這個 codebase 有兩種長得一樣、語意完全不同的 Date 值，混用就會錯一天：
 *
 * 1. **真實時刻**（`timeEntry.startTime`、`createdAt` 這種真的發生在某個瞬間的值）
 *    → 用 `toLocaleDateString()` 之類的在地化方法是對的，本來就該按使用者時區換算。
 *
 * 2. **日期標籤**（`weekStart` / `weekEnd`）——全系統慣例是
 *    `weekStart = <週一>T00:00:00.000Z`、`weekEnd = <週日>T23:59:59.999Z`。
 *    字面是 UTC，但**語意上已經是台北當天的邊界**，不該再做任何時區換算。
 *    → 必須用本檔的函式，直接取 UTC 部位。
 *
 * ⛔ 對標籤誤用 `getDate()` / `toLocaleDateString()` 會發生什麼（實測，台北時區）：
 *
 *      weekEnd = '2026-08-23T23:59:59.999Z'   // 台北的週日
 *      new Date(weekEnd).getDate()            // → 24  ❌ 跳到週一
 *      formatDateLabelShort(weekEnd)          // → '08/23'  ✅
 *
 *    申報週次就會顯示成「08/17 - 08/24」這種橫跨 8 天的區間。
 *
 * ⚠️ 這顆 bug 為什麼能活很久：**起點標籤看不出來**。`T00:00:00.000Z` 加 8 小時
 *    只到當天早上，日期部位不變；只有 `T23:59:59.999Z` 這種「日終標籤」才會跨日。
 *    所以區間的左半邊永遠是對的，只有右半邊錯——很容易被當成看錯。
 *
 * ⚠️ 也不要反過來把真實時刻丟進這裡：台北 00:00–08:00 之間發生的事會少一天。
 *
 * 後端有對稱的實作：`backend/src/utils/taipeiTime.ts` 的 `formatDateLabel`
 * （以及給真實時刻用的 `formatTaipeiDate`）。兩邊要一起改。
 */

const toDate = (value: string | Date): Date =>
  value instanceof Date ? value : new Date(value)

/** 日期標籤 → `YYYY/MM/DD` */
export const formatDateLabel = (value: string | Date): string => {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return ''
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${d.getUTCFullYear()}/${m}/${day}`
}

/** 日期標籤 → `MM/DD`（清單、卡片這種空間有限的地方用） */
export const formatDateLabelShort = (value: string | Date): string => {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return ''
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${m}/${day}`
}

/** 週次區間 → `MM/DD - MM/DD` */
export const formatDateLabelRange = (start: string | Date, end: string | Date): string =>
  `${formatDateLabelShort(start)} - ${formatDateLabelShort(end)}`

/**
 * 這一週是否跨月（weekStart 與 weekEnd 落在不同曆月）。
 *
 * ⛔ 不要自己寫 `ws.getMonth() !== we.getMonth()`——weekEnd 是日終標籤，
 *    台北時區下會被平移到隔天。「週日剛好是月底」的那幾週（2026-05-25~05-31、
 *    2027-01-25~01-31…）會被**誤判成跨月週**，畫面跳出不該出現的
 *    「🔒 跨月週審批／上月目標凍結」警語。
 *
 * 與後端 `utils/goalClosure.ts` 的 `isCrossMonthWeek` 對稱，兩邊要一起改。
 */
export const isCrossMonthWeek = (weekStart: string | Date, weekEnd: string | Date): boolean => {
  const ws = toDate(weekStart)
  const we = toDate(weekEnd)
  return (
    ws.getUTCFullYear() !== we.getUTCFullYear() ||
    ws.getUTCMonth() !== we.getUTCMonth()
  )
}

/** 日期標籤 → `YYYY 年 M 月`（跨月週警語顯示「上個月」用） */
export const formatDateLabelYearMonth = (value: string | Date): string => {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getUTCFullYear()} 年 ${d.getUTCMonth() + 1} 月`
}
